import { normalizeFullName } from "@/helpers/string/string.helpers";
import { Speaker, SpeakerAvatarVariant } from "./speakers.utility.decl";

import {
  CallDetailsFragment,
  CallTranscriptionFragment,
} from "@/graphql/generated/graphql";

/**
 * A helper function that checks if a transcription has speakers.
 * Return true if the transcription is not null, has the right type, and has a non-empty list of speakers.
 * @param transcription - transcription entity from backend
 */
export const hasSpeakers = (
  transcription: CallDetailsFragment["transcription"] | null
): transcription is CallTranscriptionFragment =>
  !!transcription &&
  transcription.__typename === "CallTranscription" &&
  !!transcription.content &&
  Array.isArray(transcription.content.speakers) &&
  transcription.content.speakers.length > 0;

const otherColors: SpeakerAvatarVariant[] = ["yellow", "purple", "pink"];

/**
 * This function takes transcription.content?.speakers, from and to
 * and return a map of speakers with their channel, variant, agentID, fullName, companyName, number
 * and the index of the other color to use for the next speaker
 */
export function mapToSpeakers(
  transcription: CallDetailsFragment["transcription"] | null,
  from: CallDetailsFragment["from"] | null,
  to: CallDetailsFragment["to"] | null
): Map<number, Speaker> | undefined {
  if (!hasSpeakers(transcription) || !from || !to) {
    return undefined;
  }

  const [map] = (transcription.content?.speakers ?? []).reduce<
    [Map<number, Speaker>, number]
  >(
    ([mapper, otherColorIndex], speaker) => {
      if (!speaker) {
        return [mapper, otherColorIndex];
      }

      if (speaker.__typename === "AgentSpeaker") {
        mapper.set(speaker.channel, {
          variant: "green",
          agentID: speaker.agentID.toString(),
          fullName:
            speaker.teammate?.entity?.__typename === "PhoneTeammate"
              ? normalizeFullName(speaker.teammate.entity.fullName)
              : undefined,
        });
        return [mapper, otherColorIndex];
      }

      if (
        speaker.__typename === "VirtualAgentSpeaker" &&
        speaker.virtualAgent?.entity?.__typename === "VirtualAgent"
      ) {
        mapper.set(speaker.channel, {
          variant: "green",
          agentID: speaker.virtualAgent.entity.ID.toString(),
          fullName: normalizeFullName(
            speaker.virtualAgent.entity.personality.name
          ),
          isVirtualAgent: true,
        });

        return [mapper, otherColorIndex];
      }

      if (
        (speaker.__typename === "ExternalSpeaker" &&
          from.__typename === "ExternalParticipant" &&
          speaker.number === from.number) ||
        (speaker.__typename === "ExternalSpeaker" &&
          to.__typename === "ExternalParticipant" &&
          speaker.number === to.number)
      ) {
        mapper.set(speaker.channel, {
          variant: "blue",
          fullName:
            speaker.external?.entity?.__typename === "PhoneContact"
              ? normalizeFullName(speaker.external.entity.fullName)
              : undefined,
          companyName:
            speaker.external?.entity?.__typename === "PhoneContact"
              ? speaker.external.entity.companyName
              : undefined,
          number: speaker.number,
        });

        return [mapper, otherColorIndex];
      }

      // If ExternalSpeaker is neither "from" or "to"
      if (speaker.__typename === "ExternalSpeaker") {
        mapper.set(speaker.channel, {
          fullName:
            speaker.external?.entity?.__typename === "PhoneContact"
              ? normalizeFullName(speaker.external.entity.fullName)
              : undefined,
          companyName:
            speaker.external?.entity?.__typename === "PhoneContact"
              ? speaker.external.entity.companyName
              : undefined,
          number: speaker.number,
          variant: otherColors[otherColorIndex % otherColors.length],
        });

        return [mapper, otherColorIndex + 1];
      }

      return [mapper, otherColorIndex];
    },
    [new Map(), 0]
  );

  return map;
}
